import { useMemo, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { Popover, PopoverContent, PopoverTrigger } from 'root/src/client/v2/common/components/ui/popover';
import { useWheelScrollRef } from 'root/src/client/v2/common/hooks/useWheelScroll';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const triggerClass = 'flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-solid border-white/[0.06] bg-white/[0.03] px-3 text-sm transition-colors hover:border-white/20 focus:border-primary/30 focus:outline-none';
const optionSearchText = (option) => {
    if (option.searchText) {
        return option.searchText;
    }
    return typeof option.label === 'string' ? option.label : '';
};
// Single-select dropdown built on the redesign Radix Popover (already a dependency). Prop-driven
// API keeps call sites compact; MUI icons replace shadcn's lucide ones (repo convention). Pass
// `searchable` to add a filter box (for long lists like campaigns).
export function Select({ value, onValueChange, options, placeholder = 'Select...', id, error, disabled, className, contentClassName, searchable, searchPlaceholder = 'Search...', }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wheelRef = useWheelScrollRef();
    const searchInputRef = useRef(null);
    const selected = options.find(option => option.value === value);
    const filtered = useMemo(() => {
        const term = query.trim().toLowerCase();
        if (!searchable || !term) {
            return options;
        }
        return options.filter(option => optionSearchText(option).toLowerCase().includes(term));
    }, [options, query, searchable]);
    const onOpenChange = (next) => {
        setOpen(next);
        if (!next) {
            setQuery('');
        }
    };
    const onSelect = (next) => {
        onValueChange(next);
        onOpenChange(false);
    };
    return (<Popover open={open} onOpenChange={onOpenChange}>
			<PopoverTrigger asChild>
				<button id={id} type="button" disabled={disabled} className={cn(triggerClass, error && 'border-destructive/60', selected ? 'text-foreground' : 'text-muted-foreground/50', className)}>
					<span className="truncate">{selected ? selected.label : placeholder}</span>
					<ExpandMoreIcon sx={{ fontSize: 18 }} className="shrink-0 text-muted-foreground"/>
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className={cn('w-[var(--radix-popover-trigger-width)] p-1', contentClassName)} onOpenAutoFocus={searchable ? (event) => {
            var _a;
            // Focus the search box on open (instead of Radix focusing the content container),
            // without the a11y-flagged `autoFocus` prop.
            event.preventDefault();
            (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        } : undefined}>
				{searchable && (<div className="relative mb-1 px-1 pt-1">
						<SearchIcon sx={{ fontSize: 16 }} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
						<input ref={searchInputRef} value={query} onChange={event => setQuery(event.target.value)} onKeyDown={(event) => {
                const first = filtered[0];
                if (event.key === 'Enter' && first) {
                    event.preventDefault();
                    onSelect(first.value);
                }
            }} placeholder={searchPlaceholder} className="h-8 w-full rounded-md border border-solid border-white/[0.06] bg-white/[0.03] pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-primary/30 focus:outline-none"/>
					</div>)}
				<div ref={wheelRef} className="max-h-64 overflow-y-auto">
					{filtered.length === 0 ? (<div className="px-2 py-6 text-center text-xs text-muted-foreground">No results</div>) : (filtered.map(option => (<button key={option.value} type="button" onClick={() => onSelect(option.value)} className={cn('flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-white/[0.06]', option.value === value ? 'text-foreground' : 'text-muted-foreground')}>
								<span className="truncate">{option.label}</span>
								{option.value === value && <CheckIcon sx={{ fontSize: 14 }} className="shrink-0 text-primary"/>}
							</button>)))}
				</div>
			</PopoverContent>
		</Popover>);
}
